(() => {
  if (!customElements.get('segastore-product-options')) {
    customElements.define('segastore-product-options', class extends HTMLElement {
      connectedCallback() {
        this.onChange = () => this.updatePalette();
        this.addEventListener('change', this.onChange);
        this.observer = new MutationObserver(() => this.updatePalette());
        this.observer.observe(this, { childList: true, subtree: true });
        this.updatePalette();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.observer?.disconnect();
      }

      updatePalette() {
        const picker = this.querySelector('variant-selects');
        if (!picker) return;
        const colourGroup = picker.querySelector('[data-segastore-option-kind="colour"]');
        const colour = colourGroup?.querySelector('input:checked, select option:checked');
        if (colour?.dataset.segastoreColour) {
          picker.style.setProperty('--segastore-variant-color', colour.dataset.segastoreColour);
          picker.style.setProperty('--segastore-variant-ink', colour.dataset.segastoreColourInk || '#ffffff');
        }
        picker.querySelectorAll('[data-segastore-option-kind]').forEach((group) => {
          const selected = group.querySelector('input:checked, select option:checked');
          const label = group.querySelector('[data-segastore-selected-value]');
          if (label && selected && label.textContent.trim() !== selected.value) label.textContent = selected.value;
        });
      }
    });
  }

  if (!customElements.get('segastore-product-story')) {
    customElements.define('segastore-product-story', class extends HTMLElement {
      connectedCallback() {
        const content = this.querySelector('[data-product-story-content]');
        if (!content) return;
        if (content.dataset.organised === 'true') {
          this.observeChapters(content);
          return;
        }
        const nodes = Array.from(content.childNodes);
        if (!nodes.some((node) => node instanceof Element && node.matches('h2, h3'))) return;
        const fragment = document.createDocumentFragment();
        const overview = document.createElement('div');
        overview.className = 'segastore-product-story__overview';
        let target = overview;
        nodes.forEach((node) => {
          if (node instanceof Element && node.matches('h2, h3')) {
            const details = document.createElement('details');
            details.className = 'segastore-product-story__chapter';
            const summary = document.createElement('summary');
            summary.append(node);
            target = document.createElement('div');
            target.className = 'segastore-product-story__chapter-body';
            details.append(summary, target);
            fragment.append(details);
          } else {
            target.append(node);
          }
        });
        if (overview.textContent.trim() || overview.querySelector('img, video')) fragment.prepend(overview);
        content.replaceChildren(fragment);
        content.dataset.organised = 'true';
        content.classList.add('segastore-product-story__content--organised');
        this.observeChapters(content);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.content?.removeEventListener('load', this.onContentLoad, true);
        cancelAnimationFrame(this.measureFrame);
      }

      observeChapters(content) {
        this.content = content;
        this.lastWidth = 0;
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(() => {
          const width = content.getBoundingClientRect().width;
          if (Math.abs(width - this.lastWidth) < 1) return;
          this.lastWidth = width;
          this.scheduleMeasure();
        });
        this.resizeObserver.observe(content);
        this.onContentLoad = () => this.scheduleMeasure();
        content.addEventListener('load', this.onContentLoad, true);
        this.scheduleMeasure();
        document.fonts?.ready.then(() => {
          if (this.isConnected) this.scheduleMeasure();
        });
      }

      scheduleMeasure() {
        cancelAnimationFrame(this.measureFrame);
        this.measureFrame = requestAnimationFrame(() => this.measureChapters());
      }

      measureChapters() {
        if (!this.isConnected || !this.content) return;
        const chapters = Array.from(this.content.querySelectorAll('.segastore-product-story__chapter'));
        const measurements = chapters.map((chapter) => {
          const body = chapter.querySelector('.segastore-product-story__chapter-body');
          if (!body || !chapter.clientWidth) return null;
          const measurement = body.cloneNode(true);
          measurement.classList.add('segastore-product-story__measure');
          measurement.setAttribute('aria-hidden', 'true');
          measurement.inert = true;
          measurement.style.width = `${chapter.clientWidth}px`;
          measurement.style.minHeight = '0';
          this.content.append(measurement);
          return measurement;
        }).filter(Boolean);
        const height = Math.ceil(Math.max(0, ...measurements.map((body) => body.getBoundingClientRect().height)));
        measurements.forEach((body) => body.remove());
        this.content.style.setProperty('--segastore-story-body-height', `${height}px`);
      }
    });
  }


  if (!customElements.get('segastore-product-navigation')) {
    customElements.define('segastore-product-navigation', class extends HTMLElement {
      connectedCallback() {
        this.onClick = (event) => {
          const link = event.target.closest('a[href^="#"]');
          if (!link || !this.contains(link) || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if (!this.revealSection(link.hash)) return;
          event.preventDefault();
          if (location.hash !== link.hash) history.pushState(null, '', link.hash);
        };
        this.onHashChange = () => this.revealSection(location.hash);
        this.addEventListener('click', this.onClick);
        window.addEventListener('hashchange', this.onHashChange);
        if (location.hash) requestAnimationFrame(() => this.revealSection(location.hash));
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        window.removeEventListener('hashchange', this.onHashChange);
        cancelAnimationFrame(this.scrollFrame);
      }

      revealSection(hash) {
        if (!['#SegastoreProductStory', '#SegastoreProductAssurance', '#judgeme_product_reviews'].includes(hash)) return false;
        const section = document.getElementById(hash.slice(1));
        if (!section) return false;
        const ancestor = section.closest('details');
        if (ancestor) ancestor.open = true;
        section.querySelectorAll('details').forEach((details) => { details.open = true; });
        const heading = section.querySelector('.segastore-product-section-heading h2, .jdgm-rev-widg__title, h2, h3') || section;
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const header = document.querySelector('sticky-header');
          const initialOffset = Math.max(16, (header?.getBoundingClientRect().bottom || 0) + 16);
          window.scrollTo({ top: Math.max(0, heading.getBoundingClientRect().top + window.scrollY - initialOffset), behavior: 'instant' });
          this.scrollFrame = requestAnimationFrame(() => {
            const headerRect = header?.getBoundingClientRect();
            const offset = headerRect && headerRect.top < window.innerHeight / 2 ? Math.max(16, headerRect.bottom + 16) : 16;
            window.scrollTo({ top: Math.max(0, heading.getBoundingClientRect().top + window.scrollY - offset), behavior: 'instant' });
            heading.focus({ preventScroll: true });
          });
        });
        return true;
      }
    });
  }

  const reviewWidget = document.getElementById('judgeme_product_reviews');
  if (reviewWidget) {
    const trimReviewOptions = () => {
      reviewWidget.querySelectorAll('.jdgm-rev-widg__sort-wrapper select.jdgm-sort-dropdown').forEach((select) => {
        const removedValues = ['with-pictures', 'pictures-first', 'videos-first'];
        const reset = removedValues.includes(select.value);
        Array.from(select.options).forEach((option) => {
          if (removedValues.includes(option.value)) option.remove();
        });
        if (reset) {
          select.value = 'most-recent';
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    };
    trimReviewOptions();
    const reviewObserver = new MutationObserver(trimReviewOptions);
    reviewObserver.observe(reviewWidget, { childList: true, subtree: true });
  }

})();
